/**
 * scenarioPushSlice — reçoit les scénarios pédagogiques poussés par le
 * formateur et les applique comme des effets temporaires dans le jeu.
 *
 * 11/02/2026 (Phase 3 — Superpouvoirs formateur).
 *
 * Effets appliqués selon `kind` :
 *   - fuel_shock : `state.fuelShock = { multiplier: 1 + magnitude, endDay }`
 *                  Combiné aux news existantes via computeDailyExpenses.
 *   - strike     : `state.companyStrike = { until: today + duration, reason: 'formateur' }`
 *                  Les contrats gèlent leur revenu pendant la période (déjà géré).
 *   - zfe_ban    : `state.zfeBan = { endDay }`
 *                  Pénalité rep + revenu -30% pour les véhicules non-électriques.
 *   - crue       : `state.treasury -= magnitude` (capex forcé) + note pédagogique.
 *
 * Chaque scénario appliqué est loggé dans `appliedScenarios[]` pour
 * traçabilité + affichage dans le journal de partie.
 */

export const createScenarioPushSlice = (set, get) => ({
  appliedScenarios: [], // { id, kind, appliedAtDay, endDay, magnitude, note }
  fuelShock: null,      // { multiplier, endDay }
  zfeBan: null,         // { endDay }
  crueEvent: null,      // { day, cost }

  /**
   * Applique un scénario poussé par le formateur. Retourne { applied: true } si
   * effectivement appliqué, false si déjà présent (idempotent par id).
   */
  applyPushedScenario: (scenario) => {
    if (!scenario || !scenario.id || !scenario.kind) return { applied: false, reason: 'invalid' };
    const state = get();
    const already = (state.appliedScenarios || []).some((s) => s.id === scenario.id);
    if (already) return { applied: false, reason: 'already_applied' };

    const today = state.totalDays || 0;
    const duration = scenario.duration_days || 30;
    const magnitude = scenario.magnitude != null ? scenario.magnitude : 0;
    const record = {
      id: scenario.id,
      kind: scenario.kind,
      appliedAtDay: today,
      endDay: today + duration,
      magnitude,
      note: scenario.note || '',
      masterHolder: scenario.master_holder || 'Responsable pédagogique',
    };

    const patch = { appliedScenarios: [...(state.appliedScenarios || []), record] };
    let msgSubject = '';
    let msgBody = '';
    let msgTone = 'urgent';

    switch (scenario.kind) {
      case 'fuel_shock':
        patch.fuelShock = { multiplier: 1 + magnitude, endDay: today + duration };
        msgSubject = 'Choc pétrolier — Scénario formateur';
        msgBody =
          `Votre formateur a poussé un choc pétrolier : le carburant renchérit de ` +
          `${Math.round(magnitude * 100)}% pendant ${duration} jours. Analysez l'impact sur votre marge et ajustez.` +
          (scenario.note ? `\n\nNote du formateur : ${scenario.note}` : '');
        break;

      case 'strike':
        patch.companyStrike = { until: today + duration, reason: 'formateur_scenario' };
        msgSubject = 'Grève sectorielle — Scénario formateur';
        msgBody =
          `Une grève sectorielle bloque la circulation pendant ${duration} jours. ` +
          `Vos contrats risquent des retards et des pénalités.` +
          (scenario.note ? `\n\nNote du formateur : ${scenario.note}` : '');
        break;

      case 'zfe_ban':
        patch.zfeBan = { endDay: today + duration };
        msgSubject = 'ZFE — Interdiction véhicules thermiques (Scénario formateur)';
        msgBody =
          `La métropole impose une ZFE pendant ${duration} jours : vos véhicules non-électriques ` +
          `subissent une pénalité de revenu de 30% sur les livraisons urbaines.` +
          (scenario.note ? `\n\nNote du formateur : ${scenario.note}` : '');
        break;

      case 'crue': {
        const cost = magnitude || 20000;
        patch.treasury = (state.treasury || 0) - cost;
        patch.crueEvent = { day: today, cost };
        msgSubject = 'Crue — Dépôt inondé (Scénario formateur)';
        msgBody =
          `Une crue majeure a inondé votre dépôt principal. Vous devez engager ${Math.round(cost)}€ ` +
          `de dépenses d'urgence pour remettre l'installation en service.` +
          (scenario.note ? `\n\nNote du formateur : ${scenario.note}` : '');
        break;
      }

      default:
        return { applied: false, reason: 'unknown_kind' };
    }

    set(patch);

    // Push message inbox si dispo (défensif).
    if (typeof get().pushCustomMessage === 'function') {
      try {
        get().pushCustomMessage({
          senderType: 'supervisor',
          senderName: record.masterHolder,
          tone: msgTone,
          kind: 'warning',
          subject: msgSubject,
          body: msgBody,
          ttlDays: 30,
          payload: { scenarioId: scenario.id, scenarioKind: scenario.kind },
        });
      } catch (err) {
        console.warn('[scenarioPushSlice] pushCustomMessage failed:', err);
      }
    }

    return { applied: true, kind: scenario.kind };
  },

  /**
   * Ticke chaque nextDay pour expirer les scénarios dont endDay est dépassé.
   * Auto-clear des flags temporaires (fuelShock, zfeBan) — idempotent.
   */
  tickScenarioExpirations: () => {
    const state = get();
    const today = state.totalDays || 0;
    const patch = {};
    if (state.fuelShock && today > state.fuelShock.endDay) {
      patch.fuelShock = null;
    }
    if (state.zfeBan && today > state.zfeBan.endDay) {
      patch.zfeBan = null;
    }
    if (Object.keys(patch).length > 0) set(patch);
  },
});
